import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Alert, Badge, Spinner, Modal, Form, Table } from "react-bootstrap";
import {
  PeopleFill,
  PersonPlus,
  PersonPlusFill,
  Eye,
  TrashFill,
  PencilSquare,
  FunnelFill,
  XCircleFill,
  CheckCircleFill,
  InfoCircleFill,
  Calendar,
  GeoAltFill,
  ClockHistory,
  HouseFill
} from 'react-bootstrap-icons';
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import SchoolRollNumberInput from "../components/SchoolRollNumberInput";

import {
  createPilot,
  createPilotsBulk,
  getPilots,
  getPilotById,
  updatePilot,
  deletePilot,
  getPilotCount,
  getCountries,
  getSchoolsByCountryId
} from "../services/APIClient";

function PilotManagement() {
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );
  const [name, setName] = useState("Admin User");

  // Loading and messaging states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data states
  const [pilots, setPilots] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableSchools, setAvailableSchools] = useState([]);
  const [pilotCounts, setPilotCounts] = useState({ total: 0, active: 0, inactive: 0 });

  // Filter states
  const [filters, setFilters] = useState({
    countryid: '',
    academic_year: '',
    active: '',
    schoolid: ''
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSchoolName, setSelectedSchoolName] = useState('');
  const [selectedSchoolRollNumber, setSelectedSchoolRollNumber] = useState('');

  // Form states
  const [editingPilot, setEditingPilot] = useState(null);
  const [viewingPilot, setViewingPilot] = useState(null);
  const [singlePilotForm, setSinglePilotForm] = useState({
    schoolid: '',
    countryid: '',
    academic_year: '',
    start_date: '',
    end_date: '',
    active: true,
    converted: false,
    notes: ''
  });

  const [bulkPilotForm, setBulkPilotForm] = useState({
    schoolids: [],
    countryid: '',
    academic_year: '',
    start_date: '',
    end_date: '',
    active: true,
    notes: ''
  });

  const [selectedSchools, setSelectedSchools] = useState(new Set());
  const [schoolSearchTerm, setSchoolSearchTerm] = useState('');

  // Handle device orientation
  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };
    window.addEventListener("resize", handleOrientationChange);
    return () => window.removeEventListener("resize", handleOrientationChange);
  }, []);

  // Load initial data
  useEffect(() => {
    setName("Admin User");
    loadPilots();
    loadCountries();
    loadCounts();
  }, []);

  // Reload pilots when filters change
  useEffect(() => {
    loadPilots();
    loadCounts();
  }, [filters]);


  // Load pilots
  const loadPilots = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.countryid) params.countryid = filters.countryid;
      if (filters.academic_year) params.academic_year = filters.academic_year;
      if (filters.active !== '') params.active = filters.active === 'true';
      if (filters.schoolid) params.schoolid = filters.schoolid;

      const data = await getPilots(params);
      setPilots(data);
    } catch (err) {
      setError(`Failed to load pilots: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load countries
  const loadCountries = async () => {
    try {
      const data = await getCountries();
      setAvailableCountries(data);
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  };

  // Load schools for bulk creation
  const loadSchools = async (countryid) => {
    if (!countryid) {
      setAvailableSchools([]);
      return;
    }
    try {
      const data = await getSchoolsByCountryId(countryid);  // Pass string directly, not object
      setAvailableSchools(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load schools:', err);
      setAvailableSchools([]);
    }
  };

  // Load counts
  const loadCounts = async () => {
    try {
      const params = {};
      if (filters.countryid) params.countryid = filters.countryid;
      if (filters.academic_year) params.academic_year = filters.academic_year;

      const [totalCount, activeCount, inactiveCount] = await Promise.all([
        getPilotCount(params),
        getPilotCount({ ...params, active: true }),
        getPilotCount({ ...params, active: false })
      ]);

      setPilotCounts({
        total: totalCount.count,
        active: activeCount.count,
        inactive: inactiveCount.count
      });
    } catch (err) {
      console.error('Failed to load counts:', err);
    }
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      countryid: '',
      academic_year: '',
      active: '',
      schoolid: ''
    });
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    setSinglePilotForm({
      schoolid: '',
      countryid: '',
      academic_year: '',
      start_date: '',
      end_date: '',
      active: true,
      converted: false,
      notes: ''
    });
    setSelectedSchoolName('');
    setSelectedSchoolRollNumber('');
    setShowCreateModal(true);
    setError(null);
    setSuccess(null);
  };

  // Open bulk modal
  const handleOpenBulkModal = () => {
    setBulkPilotForm({
      schoolids: [],
      countryid: '',
      academic_year: '',
      start_date: '',
      end_date: '',
      active: true,
      notes: ''
    });
    setSelectedSchools(new Set());
    setSchoolSearchTerm('');
    setShowBulkModal(true);
    setError(null);
    setSuccess(null);
  };

  // Handle single pilot form change
  const handleSingleFormChange = (field, value) => {
    setSinglePilotForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle bulk form change
  const handleBulkFormChange = (field, value) => {
    setBulkPilotForm(prev => ({ ...prev, [field]: value }));
    if (field === 'countryid') {
      loadSchools(value);
      setSelectedSchools(new Set());
    }
  };

  // Toggle school selection
  const handleSchoolToggle = (schoolid) => {
    setSelectedSchools(prev => {
      const newSet = new Set(prev);
      if (newSet.has(schoolid)) {
        newSet.delete(schoolid);
      } else {
        newSet.add(schoolid);
      }
      return newSet;
    });
  };

  // Select all schools
  const handleSelectAllSchools = () => {
    if (selectedSchools.size === filteredSchools.length) {
      setSelectedSchools(new Set());
    } else {
      setSelectedSchools(new Set(filteredSchools.map(s => s.id)));
    }
  };

  // Filter schools by search term
  const filteredSchools = availableSchools.filter(school =>
    school.name.toLowerCase().includes(schoolSearchTerm.toLowerCase()) ||
    (school.rollnumber && school.rollnumber.toLowerCase().includes(schoolSearchTerm.toLowerCase()))
  );

  // Create single pilot
  const handleCreatePilot = async () => {
    if (!singlePilotForm.schoolid || !singlePilotForm.countryid ||
      !singlePilotForm.academic_year || !singlePilotForm.start_date ||
      !singlePilotForm.end_date) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const dataToSend = {
        ...singlePilotForm,
        start_date: new Date(singlePilotForm.start_date).toISOString(),
        end_date: new Date(singlePilotForm.end_date).toISOString()
      };

      await createPilot(dataToSend);
      setSuccess("Pilot created successfully!");
      setShowCreateModal(false);
      loadPilots();
      loadCounts();
    } catch (err) {
      setError(`Failed to create pilot: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Create bulk pilots
  const handleCreateBulkPilots = async () => {
    if (selectedSchools.size === 0 || !bulkPilotForm.countryid ||
      !bulkPilotForm.academic_year || !bulkPilotForm.start_date ||
      !bulkPilotForm.end_date) {
      setError("Please fill in all required fields and select at least one school");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const dataToSend = {
        ...bulkPilotForm,
        schoolids: Array.from(selectedSchools),
        start_date: new Date(bulkPilotForm.start_date).toISOString(),
        end_date: new Date(bulkPilotForm.end_date).toISOString()
      };

      const result = await createPilotsBulk(dataToSend);

      let message = `Successfully created ${result.created.length} pilot(s)`;
      if (result.duplicates.length > 0) {
        message += `. ${result.duplicates.length} duplicate(s) skipped.`;
      }

      setSuccess(message);
      setShowBulkModal(false);
      loadPilots();
      loadCounts();
    } catch (err) {
      setError(`Failed to create pilots: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal
  const handleEditPilot = (pilot) => {
    setEditingPilot(pilot);
    setSinglePilotForm({
      schoolid: pilot.schoolid,
      countryid: pilot.countryid,
      academic_year: pilot.academic_year,
      start_date: new Date(pilot.start_date).toISOString().split('T')[0],
      end_date: new Date(pilot.end_date).toISOString().split('T')[0],
      active: pilot.active,
      converted: pilot.converted || false,
      notes: pilot.notes || ''
    });
    setShowEditModal(true);
    setError(null);
    setSuccess(null);
  };

  // Update pilot
  const handleUpdatePilot = async () => {
    if (!singlePilotForm.academic_year || !singlePilotForm.start_date ||
      !singlePilotForm.end_date) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const dataToSend = {
        academic_year: singlePilotForm.academic_year,
        start_date: new Date(singlePilotForm.start_date).toISOString(),
        end_date: new Date(singlePilotForm.end_date).toISOString(),
        active: singlePilotForm.active,
        converted: singlePilotForm.converted,
        notes: singlePilotForm.notes
      };

      await updatePilot(editingPilot.id, dataToSend);
      setSuccess("Pilot updated successfully!");
      setShowEditModal(false);
      setEditingPilot(null);
      loadPilots();
      loadCounts();
    } catch (err) {
      setError(`Failed to update pilot: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // View pilot details
  const handleViewDetails = async (pilot) => {
    setViewingPilot(pilot);
    setShowDetailsModal(true);
  };

  // Delete pilot
  const handleDeletePilot = async (pilotId) => {
    if (!window.confirm("Are you sure you want to delete this pilot? This action cannot be undone.")) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await deletePilot(pilotId);
      setSuccess("Pilot deleted successfully!");
      loadPilots();
      loadCounts();
    } catch (err) {
      setError(`Failed to delete pilot: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format date range
  const formatDateRange = (startDate, endDate) => {
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  return (
    <div>
      <Container fluid className="vh-100">
        {/* HeadingBanner */}
        <HeadingBanner name={`Welcome ${name}!`} isLoggedIn={true} cloud={true} />

        <Row className="h-100">
          {/* LeftPanel */}
          <LeftPanel />

          {/* Main content */}
          <Col sm={{ span: 10, offset: 2 }} className="blue-background d-flex flex-column justify-content-start align-items-center" style={{ overflowY: 'auto' }}>

            {/* Page Header */}
            <div className="w-75 mt-4 mb-3">
              <h2 className="text-white">Pilot Management</h2>
              <p className="text-white">Manage pilot programs, track participating schools, and monitor pilot status</p>
            </div>

            {/* Statistics Cards */}
            <div className="w-75 mb-4">
              <Row className="g-3">
                <Col md={4}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="text-muted mb-1">Total Pilots</h6>
                          <h3 className="mb-0">{pilotCounts.total}</h3>
                        </div>
                        <PeopleFill size={32} className="text-primary" />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="text-muted mb-1">Active Pilots</h6>
                          <h3 className="mb-0 text-success">{pilotCounts.active}</h3>
                        </div>
                        <CheckCircleFill size={32} className="text-success" />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="text-muted mb-1">Inactive Pilots</h6>
                          <h3 className="mb-0 text-muted">{pilotCounts.inactive}</h3>
                        </div>
                        <XCircleFill size={32} className="text-muted" />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Action Cards */}
            <div className="w-75 mb-4">
              <h4 className="text-white mb-3">Quick Actions</h4>
              <Row className="g-3">
                <Col md={6}>
                  <Card className="h-100 shadow-sm border-0">
                    <Card.Header className="bg-success text-white">
                      <PersonPlus className="me-2" />
                      <strong>Create Single Pilot</strong>
                    </Card.Header>
                    <Card.Body>
                      <p className="mb-3">Add a single school to a pilot program</p>
                      <Button variant="success" className="w-100" onClick={handleOpenCreateModal}>
                        <PersonPlus className="me-2" />
                        Create Pilot
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100 shadow-sm border-0">
                    <Card.Header className="bg-primary text-white">
                      <PersonPlusFill className="me-2" />
                      <strong>Create Bulk Pilots</strong>
                    </Card.Header>
                    <Card.Body>
                      <p className="mb-3">Add multiple schools to a pilot program at once</p>
                      <Button variant="primary" className="w-100" onClick={handleOpenBulkModal}>
                        <PersonPlusFill className="me-2" />
                        Bulk Create
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Error and Success Messages */}
            {error && (
              <div className="w-75 mb-3">
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  <strong>Error:</strong> {error}
                </Alert>
              </div>
            )}

            {success && (
              <div className="w-75 mb-3">
                <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                  <strong>Success:</strong> {success}
                </Alert>
              </div>
            )}

            {/* Filters and Pilots List */}
            <div className="w-75 mb-5">
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <FunnelFill className="me-2" />
                      Pilot Programs
                    </h5>
                    <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  {/* Filters */}
                  <Row className="mb-4">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Country</Form.Label>
                        <Form.Select
                          size="sm"
                          value={filters.countryid}
                          onChange={(e) => handleFilterChange('countryid', e.target.value)}
                        >
                          <option value="">All Countries</option>
                          {availableCountries.map(country => (
                            <option key={country.id} value={country.id}>{country.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Academic Year</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="e.g., 25-26"
                          value={filters.academic_year}
                          onChange={(e) => handleFilterChange('academic_year', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Status</Form.Label>
                        <Form.Select
                          size="sm"
                          value={filters.active}
                          onChange={(e) => handleFilterChange('active', e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">School ID</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Filter by school"
                          value={filters.schoolid}
                          onChange={(e) => handleFilterChange('schoolid', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Loading Spinner */}
                  {loading && (
                    <div className="text-center py-4">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2 text-muted">Loading pilots...</p>
                    </div>
                  )}

                  {/* Pilots Table */}
                  {!loading && pilots.length === 0 && (
                    <div className="text-center py-5 text-muted">
                      <InfoCircleFill size={48} className="mb-3" />
                      <p>No pilots found matching your filters</p>
                    </div>
                  )}

                  {!loading && pilots.length > 0 && (
                    <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                      <Table hover responsive>
                        <thead className="sticky-top bg-light">
                          <tr>
                            <th>School</th>
                            <th>Country</th>
                            <th>Academic Year</th>
                            <th>Date Range</th>
                            <th>Status</th>
                            <th>Converted</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pilots.map(pilot => (
                            <tr key={pilot.id}>
                              <td>
                                <HouseFill className="me-2 text-primary" />
                                {pilot.school ? (
                                  <div>
                                    <strong>{pilot.school.name}</strong>
                                    <small className="d-block text-muted font-monospace">{pilot.schoolid}</small>
                                  </div>
                                ) : (
                                  <small className="font-monospace">{pilot.schoolid}</small>
                                )}
                              </td>
                              <td>
                                <Badge bg="info">
                                  {availableCountries.find(c => c.id === pilot.countryid)?.name || pilot.countryid}
                                </Badge>
                              </td>
                              <td>
                                <Calendar className="me-2" />
                                {pilot.academic_year}
                              </td>
                              <td className="small">
                                {formatDateRange(pilot.start_date, pilot.end_date)}
                              </td>
                              <td>
                                <Badge bg={pilot.active ? "success" : "secondary"}>
                                  {pilot.active ? "Active" : "Inactive"}
                                </Badge>
                              </td>
                              <td>
                                {pilot.converted ? (
                                  <Badge bg="success">
                                    <CheckCircleFill className="me-1" />
                                    Yes
                                  </Badge>
                                ) : (
                                  <Badge bg="warning">
                                    <ClockHistory className="me-1" />
                                    No
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    onClick={() => handleViewDetails(pilot)}
                                    title="View Details"
                                  >
                                    <Eye />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-warning"
                                    onClick={() => handleEditPilot(pilot)}
                                    title="Edit"
                                  >
                                    <PencilSquare />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDeletePilot(pilot.id)}
                                    title="Delete"
                                  >
                                    <TrashFill />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Create Single Pilot Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <PersonPlus className="me-2" />
            Create Single Pilot
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>School <span className="text-danger">*</span></Form.Label>
                  <SchoolRollNumberInput
                    onSchoolIdRetrieved={(schoolid) => {
                      handleSingleFormChange('schoolid', schoolid);
                    }}
                    schoolNameRetrieved={(name, rollNumber) => {
                      setSelectedSchoolName(name);
                      setSelectedSchoolRollNumber(rollNumber);
                    }}
                  />
                  {singlePilotForm.schoolid && selectedSchoolName && (
                    <Alert variant="success" className="mt-3">
                      <CheckCircleFill className="me-2" />
                      <strong>School Selected:</strong>
                      <div className="mt-2">
                        <div><strong>Name:</strong> {selectedSchoolName}</div>
                        <div><strong>School ID:</strong> <span className="font-monospace">{singlePilotForm.schoolid}</span></div>
                        <div><strong>Roll Number:</strong> {selectedSchoolRollNumber}</div>
                      </div>
                    </Alert>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={singlePilotForm.countryid}
                    onChange={(e) => handleSingleFormChange('countryid', e.target.value)}
                  >
                    <option value="">Select country...</option>
                    {availableCountries.map(country => (
                      <option key={country.id} value={country.id}>{country.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Academic Year <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={singlePilotForm.academic_year}
                    onChange={(e) => handleSingleFormChange('academic_year', e.target.value)}
                    placeholder="e.g., 25-26"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={singlePilotForm.start_date}
                    onChange={(e) => handleSingleFormChange('start_date', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={singlePilotForm.end_date}
                    onChange={(e) => handleSingleFormChange('end_date', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="create-active"
                    label="Active"
                    checked={singlePilotForm.active}
                    onChange={(e) => handleSingleFormChange('active', e.target.checked)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="create-converted"
                    label="Converted to Customer"
                    checked={singlePilotForm.converted}
                    onChange={(e) => handleSingleFormChange('converted', e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={singlePilotForm.notes}
                onChange={(e) => handleSingleFormChange('notes', e.target.value)}
                placeholder="Optional notes about this pilot..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleCreatePilot} disabled={loading}>
            {loading && <Spinner animation="border" size="sm" className="me-2" />}
            Create Pilot
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Create Modal */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <PersonPlusFill className="me-2" />
            Bulk Create Pilots
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <InfoCircleFill className="me-2" />
            Create pilot programs for multiple schools at once with the same settings
          </Alert>

          <Form>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={bulkPilotForm.countryid}
                    onChange={(e) => handleBulkFormChange('countryid', e.target.value)}
                  >
                    <option value="">Select country...</option>
                    {availableCountries.map(country => (
                      <option key={country.id} value={country.id}>{country.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Academic Year <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={bulkPilotForm.academic_year}
                    onChange={(e) => handleBulkFormChange('academic_year', e.target.value)}
                    placeholder="25-26"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={bulkPilotForm.start_date}
                    onChange={(e) => handleBulkFormChange('start_date', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={bulkPilotForm.end_date}
                    onChange={(e) => handleBulkFormChange('end_date', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="bulk-active"
                label="Set pilots as active"
                checked={bulkPilotForm.active}
                onChange={(e) => handleBulkFormChange('active', e.target.checked)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={bulkPilotForm.notes}
                onChange={(e) => handleBulkFormChange('notes', e.target.value)}
                placeholder="Optional notes (applied to all pilots)..."
              />
            </Form.Group>

            <hr />

            <Form.Group className="mb-3">
              <Form.Label>
                Select Schools <span className="text-danger">*</span>
                <Badge bg="primary" className="ms-2">{selectedSchools.size} selected</Badge>
              </Form.Label>

              {bulkPilotForm.countryid ? (
                <>
                  <Form.Control
                    type="text"
                    placeholder="Search schools..."
                    value={schoolSearchTerm}
                    onChange={(e) => setSchoolSearchTerm(e.target.value)}
                    className="mb-2"
                  />

                  <div className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id="select-all-schools"
                      label={selectedSchools.size === filteredSchools.length ? "Deselect All" : "Select All"}
                      checked={selectedSchools.size === filteredSchools.length && filteredSchools.length > 0}
                      onChange={handleSelectAllSchools}
                      className="fw-bold"
                    />
                  </div>

                  <div style={{ maxHeight: '300px', overflowY: 'auto' }} className="border rounded p-2">
                    {filteredSchools.length === 0 ? (
                      <div className="text-center text-muted py-3">
                        No schools found for this country
                      </div>
                    ) : (
                      filteredSchools.map(school => (
                        <Form.Check
                          key={school.id}
                          type="checkbox"
                          id={`school-${school.id}`}
                          checked={selectedSchools.has(school.id)}
                          onChange={() => handleSchoolToggle(school.id)}
                          label={
                            <div>
                              <strong>{school.name}</strong>
                              {school.rollnumber && (
                                <small className="text-muted d-block">Roll: {school.rollnumber}</small>
                              )}
                            </div>
                          }
                          className="mb-2"
                        />
                      ))
                    )}
                  </div>
                </>
              ) : (
                <Alert variant="warning">
                  Please select a country first to load schools
                </Alert>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateBulkPilots}
            disabled={loading || selectedSchools.size === 0}
          >
            {loading && <Spinner animation="border" size="sm" className="me-2" />}
            Create {selectedSchools.size} Pilot(s)
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Pilot Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <PencilSquare className="me-2" />
            Edit Pilot
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingPilot && (
            <>
              <Alert variant="info">
                <InfoCircleFill className="me-2" />
                Editing pilot for school: <strong>{editingPilot.schoolid}</strong>
              </Alert>

              <Form>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Academic Year <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        value={singlePilotForm.academic_year}
                        onChange={(e) => handleSingleFormChange('academic_year', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        value={singlePilotForm.start_date}
                        onChange={(e) => handleSingleFormChange('start_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        value={singlePilotForm.end_date}
                        onChange={(e) => handleSingleFormChange('end_date', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="edit-active"
                        label="Active"
                        checked={singlePilotForm.active}
                        onChange={(e) => handleSingleFormChange('active', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="edit-converted"
                        label="Converted to Customer"
                        checked={singlePilotForm.converted}
                        onChange={(e) => handleSingleFormChange('converted', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={singlePilotForm.notes}
                    onChange={(e) => handleSingleFormChange('notes', e.target.value)}
                  />
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleUpdatePilot} disabled={loading}>
            {loading && <Spinner animation="border" size="sm" className="me-2" />}
            Update Pilot
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Eye className="me-2" />
            Pilot Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewingPilot && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>School ID:</strong>
                  <p className="font-monospace">{viewingPilot.schoolid}</p>
                </Col>
                <Col md={6}>
                  <strong>Country:</strong>
                  <p>
                    <Badge bg="info">
                      {availableCountries.find(c => c.id === viewingPilot.countryid)?.name || viewingPilot.countryid}
                    </Badge>
                  </p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Academic Year:</strong>
                  <p>{viewingPilot.academic_year}</p>
                </Col>
                <Col md={6}>
                  <strong>Status:</strong>
                  <p>
                    <Badge bg={viewingPilot.active ? "success" : "secondary"}>
                      {viewingPilot.active ? "Active" : "Inactive"}
                    </Badge>
                  </p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Start Date:</strong>
                  <p>{formatDate(viewingPilot.start_date)}</p>
                </Col>
                <Col md={6}>
                  <strong>End Date:</strong>
                  <p>{formatDate(viewingPilot.end_date)}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Converted:</strong>
                  <p>
                    {viewingPilot.converted ? (
                      <Badge bg="success">
                        <CheckCircleFill className="me-1" />
                        Yes
                      </Badge>
                    ) : (
                      <Badge bg="warning">
                        <ClockHistory className="me-1" />
                        Not Yet
                      </Badge>
                    )}
                  </p>
                </Col>
                <Col md={6}>
                  <strong>Created:</strong>
                  <p>{formatDate(viewingPilot.created_at)}</p>
                </Col>
              </Row>

              {viewingPilot.notes && (
                <Row className="mb-3">
                  <Col>
                    <strong>Notes:</strong>
                    <p className="text-muted">{viewingPilot.notes}</p>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* OrientationPrompt for portrait mode */}
      {isPortrait && <OrientationPrompt />}
    </div>
  );
}

export default PilotManagement;